import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
  Box,
  Fade
} from "@material-ui/core";
import { 
  CloudUpload as CloudUploadIcon, 
  InsertDriveFile as FileIcon, 
  Event as EventIcon 
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  uploadContainer: {
    border: "2px dashed " + theme.palette.divider,
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    backgroundColor: theme.palette.background.default,
    outline: "none",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    }
  },
  uploadContainerActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(25, 118, 210, 0.08)",
    transform: "scale(1.01)",
  },
  fileSelectedBox: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.selected,
    borderRadius: 8,
    border: "1px solid " + theme.palette.divider,
  },
  iconLarge: {
    fontSize: 48,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  menuItemDate: {
    fontWeight: 600,
    marginRight: 8,
    color: theme.palette.primary.main,
  },
  menuItemDetail: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  }
}));

const UploadFileModal = ({
  open,
  onClose,
  contactId,
  patientName,
  appointments,
  onFileUploaded,
  isDoctor
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [appointmentId, setAppointmentId] = useState("");
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setName("");
      setAppointmentId("");
      setDragActive(false);
    }
  }, [open]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelection = (selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
      if (!name) {
        setName(selectedFile.name);
      }
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.warning("Debes seleccionar un archivo");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    if (patientName) formData.append("patientName", patientName);
    if (appointmentId) formData.append("appointmentId", appointmentId);

    try {
      await api.post(
        "/medical-history/patients/" + contactId + "/files",
        formData
      );
      toast.success("Archivo subido correctamente");
      if (onFileUploaded) onFileUploaded();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Subir Archivo al Historial</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {!file ? (
              <div
                className={classes.uploadContainer + " " + (dragActive ? classes.uploadContainerActive : "")}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <CloudUploadIcon className={classes.iconLarge} color={dragActive ? "primary" : "inherit"} />
                <Typography variant="h6" color="textPrimary">
                  {dragActive ? "¡Suelta aquí!" : "Arrastra tu archivo aquí"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  o haz click para buscar en tu equipo
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={handleInputChange}
                />
              </div>
            ) : (
              <Fade in={true}>
                <div className={classes.fileSelectedBox}>
                  <FileIcon style={{ fontSize: 30, marginRight: 16, color: "#1976d2" }} />
                  <Box flexGrow={1}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <Button 
                    size="small" 
                    color="secondary" 
                    onClick={() => setFile(null)}
                  >
                    Cambiar
                  </Button>
                </div>
              </Fade>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nombre visible del archivo"
              variant="outlined"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText="Nombre con el que se guardará en el historial"
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="appointment-select-label">Vincular a Cita (Opcional)</InputLabel>
              <Select
                labelId="appointment-select-label"
                value={appointmentId}
                onChange={(e) => setAppointmentId(e.target.value)}
                label="Vincular a Cita (Opcional)"
                disabled={loading}
                // Configuración para que el menú baje y tenga scroll
                MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                    },
                    transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                    },
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                        },
                    },
                }}
              >
                <MenuItem value="">
                  <em style={{ color: "#777" }}>Sin vincular (Archivo General)</em>
                </MenuItem>

                {appointments && appointments.map((appt) => {
                  const dateStr = format(parseISO(appt.date + "T" + appt.startTime), "dd/MM/yyyy HH:mm");
                  const doctorName = !isDoctor && appt.doctor ? " - Dr. " + appt.doctor.name : "";
                  const title = appt.professionalTitle ? " - " + appt.professionalTitle.title : "";
                  const motivo = appt.motivoConsulta ? " (" + appt.motivoConsulta + ")" : "";
                  
                  return (
                    <MenuItem key={appt.id} value={appt.id}>
                      <Typography variant="body2" noWrap>
                        <span style={{ fontWeight: "bold", marginRight: 6 }}>#{appt.id}</span>
                        <span style={{ color: "#1976d2", marginRight: 6 }}>{dateStr}</span>
                        <span style={{ color: "#666" }}>
                           {title}{doctorName}{motivo}
                        </span>
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 20 }}>
        <Button onClick={onClose} color="default" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || !file}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        >
          {loading ? "Subiendo..." : "Subir Archivo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadFileModal;